import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import Iconify from 'src/components/Iconify';
import { pagesText } from 'src/constants/pagesText';
import {
  useCreateBasePolicyVersion,
  useNextBasePolicyVersion,
} from 'src/hooks/environmentWdacPolicy';
import ManagedBasePolicyForm, { DETAILS_STEP, RULE_OPTIONS_STEP } from './ManagedBasePolicyForm';

const STEP_COPY = {
  [DETAILS_STEP]: {
    label: 'Step 1 of 2 · Policy details',
    intro:
      'Build the next version of this base policy. It keeps its ID and template, and the version number moves on by itself.',
  },
  [RULE_OPTIONS_STEP]: {
    label: 'Step 2 of 2 · Rule options',
    intro: "Choose the policy's rule options. They start as the last version had them.",
  },
};

const errorMessage = error =>
  typeof error === 'string' && error ? error : pagesText.somethingWentWrong;

// Versioning a base policy the portal created, starting from the choices its last version was built
// with.
const UpdateManagedBasePolicyModal = ({ envPolicyId, open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [step, setStep] = useState(DETAILS_STEP);
  const { data: nextVersion, isFetching } = useNextBasePolicyVersion({
    envPolicyId,
    enabled: open,
  });
  const { mutateAsync: createVersion, isLoading: creating } = useCreateBasePolicyVersion();

  useEffect(() => {
    if (open) {
      setStep(DETAILS_STEP);
    }
  }, [open]);

  const handleSubmit = async payload => {
    try {
      await createVersion({ envPolicyId, payload });
      enqueueSnackbar(`${payload.policyName} updated to ${nextVersion.nextPolicyVersion}.`, {
        variant: 'success',
        autoHideDuration: 3000,
      });
      onClose();
    } catch (error) {
      // The api layer rejects with the backend's message as a plain string.
      enqueueSnackbar(errorMessage(error), { variant: 'error', autoHideDuration: 5000 });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: 'background.dialog' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Iconify icon="mdi:shield-sync-outline" width={22} height={22} color="#87C8E0" />
          Update Managed Base Policy
        </Stack>
        <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block', mt: 0.5 }}>
          {STEP_COPY[step].label}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <Typography variant="body2" sx={{ color: '#ccc' }}>
            {STEP_COPY[step].intro}
          </Typography>
          {/* The form takes its defaults once, so on opening it waits for fresh values rather than
          cached ones. The refetch a submit triggers is left alone, as the modal is closing. */}
          {!nextVersion || (isFetching && !creating) ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <ManagedBasePolicyForm
              step={step}
              onStepChange={setStep}
              loading={creating}
              onCancel={onClose}
              onSubmit={handleSubmit}
              nextVersion={nextVersion}
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

UpdateManagedBasePolicyModal.propTypes = {
  envPolicyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

export default UpdateManagedBasePolicyModal;
